import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
    Menu, X, ArrowUpRight, ChevronDown, BrainCircuit, Users, Boxes,
} from "lucide-react";

const SOLUTIONS = [
    {
        to: "/ai-solutions",
        label: "AI Solutions",
        desc: "Generative, agentic & document intelligence",
        icon: <BrainCircuit className="w-4 h-4" />,
    },
    {
        to: "/talent-solutions",
        label: "Talent Solutions",
        desc: "AI-assisted specialised staffing",
        icon: <Users className="w-4 h-4" />,
    },
    {
        to: "/product-engineering",
        label: "Product Engineering",
        desc: "Senior squads · production software",
        icon: <Boxes className="w-4 h-4" />,
    },
];

const SOLUTION_PATHS = SOLUTIONS.map((s) => s.to);

const RIGHT_LINKS = [
    { to: "/products", label: "Products" },
    { to: "/industries", label: "Industries" },
    { to: "/about", label: "About" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [solOpen, setSolOpen] = useState(false);
    const [mobileSolOpen, setMobileSolOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setSolOpen(false);
        setMobileSolOpen(false);
    }, [location.pathname]);

    const solutionsActive = SOLUTION_PATHS.includes(location.pathname);

    const linkClass = (isActive) => {
        const base = "relative px-4 py-2 text-[13.5px] font-medium tracking-tight transition-colors";
        if (scrolled) {
            return `${base} ${isActive ? "text-[#303030]" : "text-[#303030]/65 hover:text-[#303030]"}`;
        }
        return `${base} ${isActive ? "text-white" : "text-white/70 hover:text-white"}`;
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "py-2 bg-[#FAFAF8] border-b border-[#303030]/10 shadow-[0_4px_20px_-12px_rgba(48,48,48,0.18)]"
                    : "py-4 bg-transparent"
            }`}
        >
            <div className="pqx-container flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="navbar-logo-link"
                    className="flex items-center gap-2 group"
                    aria-label="Perceptiqx — Home"
                >
                    <img
                        src={scrolled ? "/brand/logo-dark.svg" : "/brand/logo-light.svg"}
                        alt="Perceptiqx"
                        className="h-7 md:h-8 w-auto transition-transform group-hover:-rotate-1"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-1" data-testid="navbar-desktop">
                    <NavLink
                        to="/"
                        end
                        data-testid="nav-link-home"
                        className={({ isActive }) => linkClass(isActive)}
                    >
                        {({ isActive }) => (
                            <>
                                Home
                                {isActive && <span className="absolute left-4 right-4 -bottom-px h-px bg-[#FF4040]" />}
                            </>
                        )}
                    </NavLink>

                    {/* Solutions dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setSolOpen(true)}
                        onMouseLeave={() => setSolOpen(false)}
                    >
                        <button
                            type="button"
                            data-testid="nav-solutions-trigger"
                            onClick={() => setSolOpen((v) => !v)}
                            aria-expanded={solOpen}
                            className={`${linkClass(solutionsActive)} inline-flex items-center gap-1`}
                        >
                            Solutions
                            <ChevronDown
                                className={`w-3.5 h-3.5 transition-transform ${solOpen ? "rotate-180" : ""}`}
                            />
                            {solutionsActive && (
                                <span className="absolute left-4 right-7 -bottom-px h-px bg-[#FF4040]" />
                            )}
                        </button>

                        {solOpen && (
                            <div
                                data-testid="nav-solutions-menu"
                                className="absolute left-0 top-full pt-3 w-[340px]"
                            >
                                <div className="rounded-2xl bg-white border border-[#303030]/10 shadow-[0_30px_60px_-24px_rgba(48,48,48,0.35)] p-2">
                                    {SOLUTIONS.map((s) => (
                                        <NavLink
                                            key={s.to}
                                            to={s.to}
                                            data-testid={`nav-link-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            className={({ isActive }) =>
                                                `flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${
                                                    isActive ? "bg-[#FF4040]/[0.06]" : "hover:bg-[#303030]/[0.04]"
                                                }`
                                            }
                                        >
                                            <span className="w-9 h-9 grid place-items-center rounded-lg bg-[#303030]/5 text-[#303030] shrink-0">
                                                {s.icon}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-[13.5px] font-semibold text-[#303030]">
                                                    {s.label}
                                                </span>
                                                <span className="block text-[11.5px] text-[#303030]/55 leading-snug mt-0.5">
                                                    {s.desc}
                                                </span>
                                            </span>
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {RIGHT_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) => linkClass(isActive)}
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    {isActive && <span className="absolute left-4 right-4 -bottom-px h-px bg-[#FF4040]" />}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid="navbar-cta-talk"
                        className={
                            scrolled
                                ? "pqx-btn-primary !py-2.5 !px-5 text-[13px]"
                                : "pqx-btn-accent !py-2.5 !px-5 text-[13px]"
                        }
                    >
                        Talk to us
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>

                <button
                    type="button"
                    data-testid="navbar-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className={`lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border ${
                        scrolled
                            ? "border-[#303030]/15 text-[#303030]"
                            : "border-white/25 text-white"
                    }`}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    className="lg:hidden border-t border-[#303030]/8 bg-[#FAFAF8]"
                    data-testid="navbar-mobile-panel"
                >
                    <div className="pqx-container py-5 flex flex-col gap-1">
                        <NavLink
                            to="/"
                            end
                            data-testid="mobile-nav-link-home"
                            className={({ isActive }) =>
                                `flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium ${
                                    isActive ? "bg-white text-[#303030] border border-[#303030]/10" : "text-[#303030]/75"
                                }`
                            }
                        >
                            Home
                            <ArrowUpRight className="w-4 h-4 opacity-60" />
                        </NavLink>

                        {/* Mobile Solutions accordion */}
                        <button
                            type="button"
                            data-testid="mobile-nav-solutions-trigger"
                            onClick={() => setMobileSolOpen((v) => !v)}
                            className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium ${
                                solutionsActive ? "text-[#303030]" : "text-[#303030]/75"
                            }`}
                        >
                            Solutions
                            <ChevronDown className={`w-4 h-4 opacity-70 transition-transform ${mobileSolOpen ? "rotate-180" : ""}`} />
                        </button>
                        {mobileSolOpen && (
                            <div className="pl-3 flex flex-col gap-1">
                                {SOLUTIONS.map((s) => (
                                    <NavLink
                                        key={s.to}
                                        to={s.to}
                                        data-testid={`mobile-nav-link-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 rounded-xl px-4 py-2.5 text-[13.5px] ${
                                                isActive ? "bg-white text-[#303030] border border-[#303030]/10" : "text-[#303030]/70"
                                            }`
                                        }
                                    >
                                        <span className="w-7 h-7 grid place-items-center rounded-lg bg-[#303030]/5 text-[#303030]">
                                            {s.icon}
                                        </span>
                                        {s.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}

                        {RIGHT_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium ${
                                        isActive ? "bg-white text-[#303030] border border-[#303030]/10" : "text-[#303030]/75"
                                    }`
                                }
                            >
                                {l.label}
                                <ArrowUpRight className="w-4 h-4 opacity-60" />
                            </NavLink>
                        ))}

                        <Link
                            to="/contact"
                            data-testid="mobile-nav-link-contact"
                            className="pqx-btn-primary mt-3"
                        >
                            Talk to us
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
